import React, { useState, useEffect, useRef } from 'react';
import { Play, Square, CheckCircle2, XCircle, Loader2, Filter, X, RefreshCw, ExternalLink } from 'lucide-react';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { formatDateTime } from '../utils/dateUtils';

interface TestCase {
  id: string;
  category: string;
  query: string;
  expectedAreas?: string[];
  expectedUrlContains?: string[];
  description: string;
  minResults?: number;
}

interface TestResult {
  testCase: TestCase;
  passed: boolean;
  results?: any[];
  resultsCount?: number;
  intent?: string;
  entities?: any;
  response?: any;
  error?: string;
  timestamp: string;
}

interface TestStats {
  total: number;
  passed: number;
  failed: number;
}

interface TestRun {
  id: string;
  timestamp: string;
  results: TestResult[];
  stats: TestStats;
  category?: string;
  testCaseIds?: string[];
}

interface TestingProps {
  testSuite?: string; // e.g., 'global-search', 'api', 'ui', etc.
  title?: string;
}

export const Testing: React.FC<TestingProps> = ({ testSuite = 'global-search', title = 'Global Search Testing' }) => {
  const { t } = useTranslation();
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedTestIds, setSelectedTestIds] = useState<string[]>([]);
  const [stats, setStats] = useState<TestStats>({ total: 0, passed: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<TestResult | null>(null);
  const [retestingId, setRetestingId] = useState<string | null>(null);
  const [testRunHistory, setTestRunHistory] = useState<TestRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const shouldStopRef = useRef(false);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    loadTestCases();
  }, []);

  const loadTestCases = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${apiUrl}/api/testing/test-cases?suite=${testSuite}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        console.log(`Loaded ${data.data.testCases.length} test cases`);
        setTestCases(data.data.testCases);
      } else {
        console.error('Failed to load test cases:', data.message);
      }
    } catch (error) {
      console.error('Error loading test cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const runSingleTest = async (testCase: TestCase) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${apiUrl}/api/testing/run-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ query: testCase.query })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      if (data.success) {
        const passed = validateTestResult(testCase, data.data.results || []);
        return {
          testCase,
          passed,
          results: data.data.results || [],
          resultsCount: data.data.resultsCount || 0,
          intent: data.data.intent,
          entities: data.data.entities,
          response: data.data.response,
          timestamp: new Date().toISOString()
        };
      }
      return {
        testCase,
        passed: false,
        error: data.message || 'Test failed',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`Error in runSingleTest for ${testCase.id}:`, error);
      return {
        testCase,
        passed: false,
        error: error instanceof Error ? error.message : 'Test failed',
        timestamp: new Date().toISOString()
      };
    }
  };

  const retestSingle = async (result: TestResult) => {
    setRetestingId(result.testCase.id);
    
    try {
      const newResult = await runSingleTest(result.testCase);
      
      // Update the result in the list
      setTestResults(prev => prev.map(r => 
        r.testCase.id === result.testCase.id ? newResult : r
      ));
      
      // Update stats
      setStats(prev => {
        const updatedResults = testResults.map(r => 
          r.testCase.id === result.testCase.id ? newResult : r
        );
        return {
          total: updatedResults.length,
          passed: updatedResults.filter(r => r.passed).length,
          failed: updatedResults.filter(r => !r.passed).length
        };
      });
      
      // Update selected result if it's the one being retested
      if (selectedResult?.testCase.id === result.testCase.id) {
        setSelectedResult(newResult);
      }
    } finally {
      setRetestingId(null);
    }
  };

  const runTests = async () => {
    if (testCases.length === 0) {
      const errorMsg = 'No test cases loaded. Please refresh the page.';
      console.error(errorMsg);
      setError(errorMsg);
      alert(errorMsg);
      return;
    }

    shouldStopRef.current = false;
    setIsRunning(true);
    setTestResults([]);
    setStats({ total: 0, passed: 0, failed: 0 });
    setError(null);

    const testsToRun = selectedTestIds.length > 0
      ? testCases.filter(tc => selectedTestIds.includes(tc.id))
      : selectedCategory
      ? testCases.filter(tc => tc.category.toLowerCase() === selectedCategory.toLowerCase())
      : testCases;

    console.log(`Running ${testsToRun.length} test(s)...`);

    if (testsToRun.length === 0) {
      const errorMsg = 'No tests selected to run';
      console.error(errorMsg);
      setError(errorMsg);
      setIsRunning(false);
      return;
    }

    const results: TestResult[] = [];
    const delayBetweenTests = 500;

    try {
      for (let i = 0; i < testsToRun.length; i++) {
        // Check if stopped
        if (shouldStopRef.current) {
          console.log('Tests stopped by user');
          break;
        }

        const testCase = testsToRun[i];
        console.log(`Running test ${i + 1}/${testsToRun.length}: ${testCase.id} - ${testCase.query}`);
        
        // Small delay to avoid rate limiting
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenTests));
        }

        try {
          const result = await runSingleTest(testCase);
          results.push(result);
          console.log(`Test ${testCase.id} ${result.passed ? 'PASSED' : 'FAILED'}`);
        } catch (error) {
          console.error(`Error running test ${testCase.id}:`, error);
          results.push({
            testCase,
            passed: false,
            error: error instanceof Error ? error.message : 'Test execution failed',
            timestamp: new Date().toISOString()
          });
        }
        
        // Update results in real-time
        setTestResults([...results]);
        
        // Update stats
        const newStats = {
          total: results.length,
          passed: results.filter(r => r.passed).length,
          failed: results.filter(r => !r.passed).length
        };
        setStats(newStats);
      }
    } catch (error) {
      console.error('Error in test execution loop:', error);
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsRunning(false);
      shouldStopRef.current = false;
      console.log(`Tests completed. ${results.filter(r => r.passed).length}/${results.length} passed`);
      
      // Save to history
      const runId = `run-${Date.now()}`;
      const newRun: TestRun = {
        id: runId,
        timestamp: new Date().toISOString(),
        results: results,
        stats: {
          total: results.length,
          passed: results.filter(r => r.passed).length,
          failed: results.filter(r => !r.passed).length
        },
        category: selectedCategory || undefined,
        testCaseIds: selectedTestIds.length > 0 ? selectedTestIds : undefined
      };
      
      setTestRunHistory(prev => [newRun, ...prev].slice(0, 50)); // Keep last 50 runs
      setSelectedRunId(runId);
    }
  };

  const loadTestRun = (runId: string) => {
    const run = testRunHistory.find(r => r.id === runId);
    if (run) {
      setTestResults(run.results);
      setStats(run.stats);
      setSelectedRunId(runId);
    }
  };

  const validateTestResult = (testCase: TestCase, results: any[]): boolean => {
    if (testCase.minResults !== undefined && results.length < testCase.minResults) {
      return false;
    }

    if (testCase.expectedAreas && testCase.expectedAreas.length > 0) {
      const foundAreas = results.map(r => r.area);
      const missingAreas = testCase.expectedAreas.filter(
        area => !foundAreas.some(fa => fa.toLowerCase().includes(area.toLowerCase()))
      );
      if (missingAreas.length > 0) {
        return false;
      }
    }

    if (testCase.expectedUrlContains && testCase.expectedUrlContains.length > 0) {
      const allUrls = results.map(r => r.url).join(' ');
      const missingPatterns = testCase.expectedUrlContains.filter(
        pattern => !allUrls.toLowerCase().includes(pattern.toLowerCase())
      );
      if (missingPatterns.length > 0 && results.length > 0) {
        return false;
      }
    }

    if (results.length === 0 && testCase.expectedAreas) {
      return false;
    }

    return true;
  };

  const categories = Array.from(new Set(testCases.map(tc => tc.category)));

  const filteredTestCases = selectedCategory
    ? testCases.filter(tc => tc.category.toLowerCase() === selectedCategory.toLowerCase())
    : testCases;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          {title}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Test suite: {testSuite}
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedTestIds([]);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Categories ({testCases.length})</option>
              {categories.map(cat => {
                const count = testCases.filter(tc => tc.category === cat).length;
                return (
                  <option key={cat} value={cat}>
                    {cat} ({count})
                  </option>
                );
              })}
            </select>
          </div>

          <button
            onClick={runTests}
            disabled={isRunning}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Running Tests...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run {selectedCategory ? `${filteredTestCases.length} ` : 'All '}Tests
              </>
            )}
          </button>

          {isRunning && (
            <button
              onClick={() => {
                console.log('Stop button clicked');
                shouldStopRef.current = true;
                setIsRunning(false);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700"
            >
              <Square className="w-4 h-4" />
              Stop
            </button>
          )}
          
          {error && (
            <div className="text-sm text-error-600 dark:text-error-400 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 px-3 py-2 rounded">
              {error}
            </div>
          )}
          
          {testCases.length === 0 && !loading && (
            <div className="text-sm text-error-600 dark:text-error-400">
              No test cases loaded. Check console for errors.
            </div>
          )}
        </div>
      </div>

      {/* Test Run History */}
      {testRunHistory.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Test Run History</h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">{testRunHistory.length} runs</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {testRunHistory.map((run) => (
              <button
                key={run.id}
                onClick={() => loadTestRun(run.id)}
                className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                  selectedRunId === run.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>{formatDateTime(run.timestamp)}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                    run.stats.failed === 0
                      ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                      : 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300'
                  }`}>
                    {run.stats.passed}/{run.stats.total}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      {stats.total > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Tests</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Passed</div>
            <div className="text-2xl font-bold text-success-600">{stats.passed}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
            <div className="text-2xl font-bold text-error-600">{stats.failed}</div>
          </div>
        </div>
      )}

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-2 mb-6">
          {testResults.map((result, index) => (
            <div
              key={result.testCase.id}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 cursor-pointer hover:shadow-lg transition-all ${
                result.passed 
                  ? 'border-green-500 hover:border-green-600' 
                  : 'border-red-500 hover:border-red-600 bg-red-50/50 dark:bg-error-900/10'
              }`}
              onClick={() => setSelectedResult(result)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {result.passed ? (
                      <CheckCircle2 className="w-5 h-5 text-success-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-error-500" />
                    )}
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      [{result.testCase.id}] {result.testCase.category}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {result.testCase.description}
                  </div>
                  <div className="text-sm font-mono text-gray-700 dark:text-gray-300 mb-2">
                    Query: "{result.testCase.query}"
                  </div>
                  
                  {/* Intent */}
                  {result.intent && (
                    <div className="mb-2">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Intent: </span>
                      <span className="text-xs px-2 py-1 bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 rounded">
                        {result.intent}
                      </span>
                    </div>
                  )}
                  
                  {/* Entities */}
                  {result.entities && Object.keys(result.entities).length > 0 && (
                    <div className="mb-2">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Entities: </span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {Object.entries(result.entities).map(([key, value]) => (
                          <span
                            key={key}
                            className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded"
                          >
                            {key}: {String(value)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {result.error && (
                    <div className="text-sm text-error-600 dark:text-error-400">
                      Error: {result.error}
                    </div>
                  )}
                  
                  {result.results && result.results.length > 0 && (
                    <div className="mt-2">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Results ({result.resultsCount}):
                      </div>
                      <div className="space-y-1">
                        {result.results.map((r, idx) => (
                          <div key={idx} className="text-sm text-gray-700 dark:text-gray-300 pl-4">
                            • <span className="font-semibold">[{r.area}]</span> {r.fullPath} -{' '}
                            <a
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent-600 dark:text-accent-400 hover:underline"
                              onClick={(e) => {
                                e.preventDefault();
                                // Use navigation context if available, otherwise use window.location
                                if (r.url.startsWith('/')) {
                                  window.location.href = r.url;
                                } else {
                                  window.open(r.url, '_blank');
                                }
                              }}
                            >
                              {r.url}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {result.results && result.results.length === 0 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      No results returned
                    </div>
                  )}
                  
                  {/* Full Response (collapsible) */}
                  {result.response && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                        View Full Response
                      </summary>
                      <pre className="mt-1 p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs overflow-auto max-h-48">
                        {JSON.stringify(result.response, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Test Detail Modal */}
      {selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedResult(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                {selectedResult.passed ? (
                  <CheckCircle2 className="w-6 h-6 text-success-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-error-500" />
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    [{selectedResult.testCase.id}] {selectedResult.testCase.category}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedResult.testCase.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => retestSingle(selectedResult)}
                  disabled={retestingId === selectedResult.testCase.id}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {retestingId === selectedResult.testCase.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Retesting...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Retest
                    </>
                  )}
                </button>
                <button
                  onClick={() => setSelectedResult(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Query */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Query</h3>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded font-mono text-sm text-gray-900 dark:text-gray-100">
                  "{selectedResult.testCase.query}"
                </div>
              </div>

              {/* Intent */}
              {selectedResult.intent && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Intent</h3>
                  <div className="px-3 py-2 bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 rounded inline-block">
                    {selectedResult.intent}
                  </div>
                </div>
              )}

              {/* Entities */}
              {selectedResult.entities && Object.keys(selectedResult.entities).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Entities</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(selectedResult.entities).map(([key, value]) => (
                      <div
                        key={key}
                        className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-sm"
                      >
                        <span className="font-semibold">{key}:</span> {String(value)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Results */}
              {selectedResult.results && selectedResult.results.length > 0 ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Results ({selectedResult.resultsCount})
                  </h3>
                  <div className="space-y-2">
                    {selectedResult.results.map((r, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-gray-900 dark:text-gray-100">[{r.area}]</span>
                              <span className="text-gray-700 dark:text-gray-300">{r.fullPath}</span>
                            </div>
                            {r.description && (
                              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {r.description}
                              </div>
                            )}
                            <a
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-accent-600 dark:text-accent-400 hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (r.url.startsWith('/')) {
                                  window.location.href = r.url;
                                } else {
                                  window.open(r.url, '_blank');
                                }
                              }}
                            >
                              {r.url}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Results</h3>
                  <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded">
                    No results returned
                  </div>
                </div>
              )}

              {/* Error */}
              {selectedResult.error && (
                <div>
                  <h3 className="text-sm font-semibold text-error-600 dark:text-error-400 mb-2">Error</h3>
                  <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 p-3 rounded text-sm text-error-700 dark:text-error-300">
                    {selectedResult.error}
                  </div>
                </div>
              )}

              {/* Full Response */}
              {selectedResult.response && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Full Response</h3>
                  <pre className="bg-gray-50 dark:bg-gray-700 p-4 rounded text-xs overflow-auto max-h-64 border border-gray-200 dark:border-gray-600">
                    {JSON.stringify(selectedResult.response, null, 2)}
                  </pre>
                </div>
              )}

              {/* Test Case Details */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Test Case Details</h3>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded text-sm space-y-1">
                  <div><span className="font-semibold">ID:</span> {selectedResult.testCase.id}</div>
                  <div><span className="font-semibold">Category:</span> {selectedResult.testCase.category}</div>
                  {selectedResult.testCase.expectedAreas && (
                    <div>
                      <span className="font-semibold">Expected Areas:</span>{' '}
                      {selectedResult.testCase.expectedAreas.join(', ')}
                    </div>
                  )}
                  {selectedResult.testCase.expectedUrlContains && (
                    <div>
                      <span className="font-semibold">Expected URL Patterns:</span>{' '}
                      {selectedResult.testCase.expectedUrlContains.join(', ')}
                    </div>
                  )}
                  {selectedResult.testCase.minResults !== undefined && (
                    <div>
                      <span className="font-semibold">Minimum Results:</span> {selectedResult.testCase.minResults}
                    </div>
                  )}
                  <div><span className="font-semibold">Timestamp:</span> {formatDateTime(selectedResult.timestamp)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Cases List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Test Cases ({filteredTestCases.length})
          </h2>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
          {filteredTestCases.map(testCase => (
            <div
              key={testCase.id}
              className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              onClick={() => {
                if (selectedTestIds.includes(testCase.id)) {
                  setSelectedTestIds(selectedTestIds.filter(id => id !== testCase.id));
                } else {
                  setSelectedTestIds([...selectedTestIds, testCase.id]);
                }
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <input
                      type="checkbox"
                      checked={selectedTestIds.includes(testCase.id)}
                      onChange={() => {}}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      [{testCase.id}] {testCase.category}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {testCase.description}
                  </div>
                  <div className="text-xs font-mono text-gray-500 dark:text-gray-400">
                    "{testCase.query}"
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

